import { Matrix4, Vector3 } from 'three';
import { calculateCentroid, Clustering } from '../../Utils';
/**
 * Apply the mesh translation and rotation to a vertex
 */
function transformVertex(vertex, translation, rotation) {
    return vertex.clone().applyQuaternion(rotation).add(translation);
}
/**
 * Recenter a world object based on a list of points
 */
function recenterObject(object, points) {
    // Recenter the points in-place
    const centroid = calculateCentroid(points.map(point => new Vector3(point.x, point.y, point.z)));
    for (const point of points) {
        point.x -= centroid.x;
        point.y -= centroid.y;
        point.z -= centroid.z;
    }
    object.setPositionECEF(centroid);
}
/**
 * Checks if a batchable mesh is compatible with a batch key
 */
function isInBatch(batchable, batchKey) {
    return (batchable.material.type === batchKey.material.type &&
        batchable.material.pointSize === batchKey.material.pointSize &&
        batchable.material.faceCulling === batchKey.material.faceCulling &&
        batchable.material.wireframe === batchKey.material.wireframe &&
        batchable.material.vertexColors === batchKey.material.vertexColors &&
        batchable.scale.x === batchKey.scale.x &&
        batchable.scale.y === batchKey.scale.y &&
        batchable.scale.z === batchKey.scale.z &&
        batchable.renderLayer === batchKey.renderLayer);
}
/**
 * Create a new batch key from a given batchable
 */
function createBatchKey(batchable, mesh) {
    return {
        mesh,
        material: structuredClone(batchable.material),
        scale: batchable.scale.clone(),
        renderLayer: batchable.renderLayer,
    };
}
/**
 * Create the render object for a batch
 */
function createBatchObject(renderer, batchable, mesh) {
    const object = renderer.getObjectFactory().createRawMeshObject({
        mesh,
        position: {
            ecef: { x: 0, y: 0, z: 0 },
        },
        scale: batchable.scale,
        material: batchable.material,
    });
    object.setRenderLayer(batchable.renderLayer);
    return object;
}
/**
 * Batch of geometries.
 */
class BatchCore {
    constructor(renderer, pickingMap) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pickingMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "members", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pointBatches", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lineBatches", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stripBatches", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "polygonBatches", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.pickingMap = pickingMap;
        this.members = [];
        this.pointBatches = new Map();
        this.lineBatches = new Map();
        this.stripBatches = new Map();
        this.polygonBatches = new Map();
    }
    /**
     * Recenter for all batched objects to their centroids.
     */
    recenter() {
        for (const [key, object] of this.pointBatches) {
            recenterObject(object, key.mesh.points);
        }
        for (const [key, object] of this.lineBatches) {
            recenterObject(object, key.mesh.vertices);
        }
        for (const [key, object] of this.polygonBatches) {
            recenterObject(object, key.mesh.vertices);
        }
        for (const [key, object] of this.stripBatches) {
            recenterObject(object, key.mesh.vertices);
        }
    }
    /**
     * Append a new point mesh.
     *
     * @param batchable
     */
    appendPoints(batchable) {
        // Find a compatible batch key
        let compatibleKey = null;
        for (const [key, _] of this.pointBatches) {
            if (isInBatch(batchable, key)) {
                compatibleKey = key;
            }
        }
        // If no key found, create a new batch
        if (compatibleKey === null) {
            const mesh = this.renderer.createPointMesh();
            mesh.color = [];
            mesh.pickingUserId = [];
            compatibleKey = createBatchKey(batchable, mesh);
            const object = createBatchObject(this.renderer, batchable, mesh);
            this.pointBatches.set(compatibleKey, object);
        }
        // Update the mesh of that batch
        const mesh = compatibleKey.mesh;
        const pickingId = this.pickingMap.get(batchable);
        if (pickingId !== undefined) {
            for (const vertex of batchable.vertices) {
                const transformed = transformVertex(vertex, batchable.position, batchable.rotation);
                mesh.points.push(transformed);
                mesh.pickingUserId?.push(pickingId);
            }
            for (const color of batchable.colors) {
                mesh.color?.push(color);
            }
        }
    }
    /**
     * Append a new line mesh.
     *
     * @param batchable
     */
    appendLines(batchable) {
        // Find a compatible batch key
        let compatibleKey = null;
        for (const [key, _] of this.lineBatches) {
            if (isInBatch(batchable, key)) {
                compatibleKey = key;
            }
        }
        // If no key found, create a new batch
        if (compatibleKey === null) {
            const mesh = this.renderer.createLineMesh();
            mesh.color = [];
            mesh.pickingUserId = [];
            compatibleKey = createBatchKey(batchable, mesh);
            const object = createBatchObject(this.renderer, batchable, mesh);
            this.lineBatches.set(compatibleKey, object);
        }
        // Update the mesh of that batch
        const mesh = compatibleKey.mesh;
        const pickingId = this.pickingMap.get(batchable);
        if (pickingId !== undefined) {
            for (const vertex of batchable.vertices) {
                const transformed = transformVertex(vertex, batchable.position, batchable.rotation);
                mesh.vertices.push(transformed);
                mesh.pickingUserId?.push(pickingId);
            }
            for (const color of batchable.colors) {
                mesh.color?.push(color);
            }
        }
    }
    /**
     * Append a new line strip mesh.
     *
     * @param batchable
     */
    appendStrip(batchable) {
        // Find a compatible batch key
        let compatibleKey = null;
        for (const [key, _] of this.stripBatches) {
            if (isInBatch(batchable, key)) {
                compatibleKey = key;
            }
        }
        // If no key found, create a new batch
        if (compatibleKey === null) {
            const mesh = this.renderer.createLineStripMesh();
            mesh.color = [];
            mesh.pickingUserId = [];
            compatibleKey = createBatchKey(batchable, mesh);
            const object = createBatchObject(this.renderer, batchable, mesh);
            this.stripBatches.set(compatibleKey, object);
        }
        // Update the mesh of that batch
        const mesh = compatibleKey.mesh;
        const pickingId = this.pickingMap.get(batchable);
        if (pickingId !== undefined) {
            for (const vertex of batchable.vertices) {
                const transformed = transformVertex(vertex, batchable.position, batchable.rotation);
                mesh.vertices.push(transformed);
                mesh.pickingUserId?.push(pickingId);
            }
            for (const color of batchable.colors) {
                mesh.color?.push(color);
            }
        }
    }
    /**
     * Append a new polygon mesh.
     *
     * @param batchable
     */
    appendPolygon(batchable) {
        // Find a compatible batch key
        let compatibleKey = null;
        for (const [key, _] of this.polygonBatches) {
            if (isInBatch(batchable, key)) {
                compatibleKey = key;
            }
        }
        // If no key found, create a new batch
        if (compatibleKey === null) {
            const mesh = this.renderer.createIndexedTriangleMesh();
            mesh.color = [];
            mesh.pickingUserId = [];
            compatibleKey = createBatchKey(batchable, mesh);
            const object = createBatchObject(this.renderer, batchable, mesh);
            this.polygonBatches.set(compatibleKey, object);
        }
        // Update the mesh of that batch
        const mesh = compatibleKey.mesh;
        const pickingId = this.pickingMap.get(batchable);
        if (pickingId !== undefined) {
            const offset = mesh.vertices.length;
            for (const index of batchable.indices) {
                mesh.indices.push(index + offset);
            }
            for (const vertex of batchable.vertices) {
                const transformed = transformVertex(vertex, batchable.position, batchable.rotation);
                mesh.vertices.push(transformed);
                mesh.pickingUserId?.push(pickingId);
            }
            for (const color of batchable.colors) {
                mesh.color?.push(color);
            }
        }
    }
    /**
     * Render the world objects.
     */
    renderObjects() {
        for (const [_, object] of this.pointBatches) {
            this.renderer.getWorld().addObject(object);
        }
        for (const [_, object] of this.lineBatches) {
            this.renderer.getWorld().addObject(object);
        }
        for (const [_, object] of this.polygonBatches) {
            this.renderer.getWorld().addObject(object);
        }
        for (const [_, object] of this.stripBatches) {
            this.renderer.getWorld().addObject(object);
        }
    }
    /**
     * Add a batchable to this batch.
     *
     * @param batchable
     */
    add(batchable) {
        this.members.push(batchable);
    }
    /**
     * Build the actual batch geometries.
     */
    build() {
        for (const batchable of this.members) {
            switch (batchable.type) {
                case 'points':
                    this.appendPoints(batchable);
                    break;
                case 'lines':
                    this.appendLines(batchable);
                    break;
                case 'strip':
                    this.appendStrip(batchable);
                    break;
                case 'polygon':
                    this.appendPolygon(batchable);
                    break;
            }
        }
        this.recenter();
        for (const [key, _] of this.pointBatches) {
            key.mesh.resolve();
        }
        for (const [key, _] of this.lineBatches) {
            key.mesh.resolve();
        }
        for (const [key, _] of this.stripBatches) {
            key.mesh.resolve();
        }
        for (const [key, _] of this.polygonBatches) {
            key.mesh.resolve();
        }
        this.renderObjects();
    }
    /**
     * Clear existing mesh batches.
     */
    clear() {
        this.members = [];
        for (const [key, _] of this.pointBatches) {
            key.mesh.points = [];
            key.mesh.color = [];
            key.mesh.pickingUserId = [];
        }
        for (const [key, _] of this.lineBatches) {
            key.mesh.vertices = [];
            key.mesh.color = [];
            key.mesh.pickingUserId = [];
        }
        for (const [key, _] of this.stripBatches) {
            key.mesh.vertices = [];
            key.mesh.color = [];
            key.mesh.pickingUserId = [];
        }
        for (const [key, _] of this.polygonBatches) {
            key.mesh.vertices = [];
            key.mesh.indices = [];
            key.mesh.color = [];
            key.mesh.pickingUserId = [];
        }
    }
}
/**
 * Handles rendering batched mesh objects
 *
 * Meshes are batched based on material, size attenuation, and scale
 */
export class MeshBatch {
    constructor(renderer) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "clusterer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "batchables", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "batches", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pickingMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.clusterer = new Clustering(100000);
        this.batchables = new Set();
        this.batches = [];
        this.pickingMap = new Map();
    }
    /**
     * Compute the cluster point of a batchable.
     *
     * @param batchable
     */
    computeClusterPoint(batchable) {
        const transform = new Matrix4().compose(batchable.position, batchable.rotation, batchable.scale);
        return calculateCentroid(batchable.vertices).applyMatrix4(transform);
    }
    /**
     * Get the batchID of a batchable.
     *
     * @param batchable
     * @returns
     */
    getBatchID(batchable) {
        const clusterPoint = this.computeClusterPoint(batchable);
        const clusterID = this.clusterer.add(clusterPoint);
        if (clusterID === this.batches.length) {
            this.batches.push(new BatchCore(this.renderer, this.pickingMap));
        }
        return clusterID;
    }
    /**
     * Add a mesh object to be batched. If the mesh is already batched, it will still refresh the batches.
     *
     * This should be called whenever a batchable mesh is updated (e.g., new material).
     *
     * Updates are not propagated until `refresh()` is invoked.
     *
     * @param batchable
     */
    addMesh(batchable) {
        const pickingManager = this.renderer.pickingManager;
        if (!this.batchables.has(batchable)) {
            this.batchables.add(batchable);
            this.pickingMap.set(batchable, pickingManager.newId());
        }
    }
    /**
     * Remove a batched mesh object.
     *
     * Updates are not propagated until `refresh()` is invoked.
     *
     * @param batchable
     */
    removeMesh(batchable) {
        this.batchables.delete(batchable);
        this.pickingMap.delete(batchable);
    }
    /**
     * This will regenerate the batch objects and recenter using the updated mesh list.
     *
     * Dot NOT call this in a runtime loop.
     */
    refresh() {
        this.clusterer.clear();
        for (const batch of this.batches) {
            batch.clear();
        }
        for (const batchable of this.batchables) {
            const batchID = this.getBatchID(batchable);
            this.batches[batchID].add(batchable);
        }
        for (const batch of this.batches) {
            batch.build();
        }
    }
    /**
     * Get the picking ID of a batchable.
     *
     * @param batchable
     * @returns
     */
    getPickingId(batchable) {
        return this.pickingMap.get(batchable);
    }
}
